/** Search the register AND the sheets. Local index, stated limits.
 *
 * Two things are shown that a search UI usually hides: whether a hit came from embedded
 * text or from OCR (an OCR hit needs confirming), and how much of the project is actually
 * indexed — because "no results" means something different when half the renditions have
 * never been read.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search as SearchIcon } from 'lucide-react';
import api from '@/lib/api';
import { useNfaTheme } from '@/components/nfa/nfaTheme';
import { Btn, Empty, Panel, Stat } from '@/components/nfa/NfaPrimitives';

const FACETS = [['discipline', 'Discipline'], ['doc_type', 'Type'],
  ['area', 'Area'], ['system', 'System']];

const EdmSearch = ({ projectId }) => {
  const T = useNfaTheme();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [fullText, setFullText] = useState(true);
  const [picked, setPicked] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    const p = new URLSearchParams({ full_text: String(fullText) });
    if (q) p.set('q', q);
    Object.entries(picked).forEach(([k, v]) => v && p.set(k, v));
    api.get(`/edm/projects/${projectId}/search?${p}`)
      .then((r) => setData(r.data)).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [projectId, q, fullText, picked]);
  useEffect(() => { const h = setTimeout(load, q ? 350 : 0); return () => clearTimeout(h); },
    [load, q]);

  const c = data?.coverage || {};
  return (
    <div className="space-y-3" data-testid="edm-search">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat T={T} label="Matches" value={data?.count ?? '—'} testId="edm-search-stat-hits"
          info="Documents matching the filters, and the search term where one is given." />
        <Stat T={T} label="Indexed renditions" value={c.indexedFiles ?? '—'}
          sub={`of ${c.pdfFiles ?? '—'} PDFs`} testId="edm-search-stat-indexed"
          info={c.statement} />
        <Stat T={T} label="Engine" value="Mongo text" sub="lexical, local"
          testId="edm-search-stat-engine" info={data?.engine?.statement} />
        <Stat T={T} label="Full text" value={fullText ? 'on' : 'off'}
          testId="edm-search-stat-fulltext"
          info="With full text ON the search reads the sheets as well as the register.
                With it OFF only numbers and titles are matched." />
      </div>

      <Panel T={T} testId="edm-search-panel" pad={false} title="Search"
        subtitle={data?.engine?.statement}
        right={<>
          <div className="relative">
            <SearchIcon className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2"
              style={{ color: T.muted }} />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="edm-search-input" placeholder="a line number, a note, a tag…"
              className="pl-7 pr-2 py-1.5 rounded-md text-[11px] outline-none w-60"
              style={{ background: T.isDark ? T.surfaceAlt : '#FFF',
                border: `1px solid ${T.border}`, color: T.ink }} />
          </div>
          <Btn T={T} variant={fullText ? 'primary' : 'ghost'} testId="edm-search-fulltext"
            onClick={() => setFullText((v) => !v)}>Full text</Btn>
        </>}>
        <div className="px-4 py-2 flex flex-wrap gap-3"
          style={{ borderBottom: `1px solid ${T.border}` }} data-testid="edm-search-facets">
          {FACETS.map(([key, label]) => (
            <div key={key}>
              <div className="text-[9px] uppercase tracking-[0.08em] mb-1"
                style={{ color: T.muted }}>{label}</div>
              <div className="flex flex-wrap gap-1">
                {(data?.facets?.[key] || []).slice(0, 8).map((f) => (
                  <button key={f.value} type="button"
                    data-testid={`edm-facet-${key}-${f.value}`}
                    onClick={() => setPicked((p) => ({
                      ...p, [key]: p[key] === f.value ? '' : f.value }))}
                    className="px-1.5 py-0.5 rounded text-[10px]"
                    style={picked[key] === f.value
                      ? { background: T.primary, color: '#fff' }
                      : { background: T.surfaceAlt, color: T.body,
                        border: `1px solid ${T.border}` }}>
                    {f.value} · {f.count}
                  </button>
                ))}
                {(data?.facets?.[key] || []).length === 0 && (
                  <span className="text-[10px]" style={{ color: T.muted }}>none</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {loading && <Empty T={T}><Loader2 className="w-4 h-4 animate-spin inline" /> Searching…</Empty>}
        {!loading && (data?.hits || []).length === 0 && (
          <Empty T={T} testId="edm-search-empty">
            Nothing matched. {c.statement}
          </Empty>
        )}
        {!loading && (data?.hits || []).map((h) => (
          <div key={h.document_id} data-testid={`edm-search-hit-${h.document_no}`}
            className="px-4 py-2.5 text-[11.5px]"
            style={{ borderBottom: `1px solid ${T.border}` }}>
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" className="font-mono hover:underline"
                style={{ color: T.primary }}
                data-testid={`edm-search-open-${h.document_no}`}
                onClick={() => navigate(`/engineering/documents/${h.document_id}`)}>
                {h.document_no}
              </button>
              <span style={{ color: T.ink }}>{h.title}</span>
              <span className="text-[9.5px] px-1.5 py-0.5 rounded"
                style={{ background: T.surfaceAlt, color: T.muted }}>
                {h.matchedOn}
              </span>
              {h.textHit?.confirmBeforeRelying && (
                <span className="text-[9.5px] px-1.5 py-0.5 rounded"
                  data-testid={`edm-search-ocr-${h.document_no}`}
                  style={{ background: T.tint(T.amber), color: T.amber }}>
                  found by OCR · confirm against the sheet
                </span>
              )}
            </div>
            {h.textHit?.snippet && (
              <div className="mt-1 text-[10.5px]" style={{ color: T.body }}
                data-testid={`edm-search-snippet-${h.document_no}`}>
                {h.textHit.page ? `p.${h.textHit.page} · ` : ''}{h.textHit.snippet}
              </div>
            )}
          </div>
        ))}
      </Panel>
    </div>
  );
};

export default EdmSearch;
